import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Grid,
    Paper,
    IconButton,
    Divider,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    alpha,
    useTheme,
    Fade,
    Grow,
    Stack,
    Avatar,
    TextField,
    InputAdornment
} from '@mui/material';
import {
    Add as AddIcon,
    FileDownloadOutlined as FileDownloadIcon,
    CalendarTodayOutlined as CalendarTodayIcon,
    GridViewOutlined as GridViewIcon,
    FormatListBulleted as FormatListBulletedIcon,
    FilterList as FilterListIcon,
    MoreVert as MoreVertIcon,
    PhoneOutlined as PhoneIcon,
    EmailOutlined as EmailIcon,
    LocationOnOutlined as LocationIcon,
    Search as SearchIcon,
    Business as BusinessIcon
} from '@mui/icons-material';
import clgimg from '../Assests/clgimg.png';
import { getActiveColleges } from '../Apis/CollegeApis';

const ItiCard = ({ data }) => {
    const theme = useTheme();
    const navigate = useNavigate();

    return (
        <Grow in={true} timeout={500}>
            <Paper
                elevation={0}
                sx={{
                    p: 2.5,
                    borderRadius: 5,
                    border: '1px solid #E2E8F0',
                    bgcolor: 'white',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.05)',
                        borderColor: theme.palette.primary.main
                    }
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="caption" fontWeight="900" sx={{ color: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.08), px: 1.5, py: 0.5, borderRadius: 2 }}>
                        {data.code || 'CODE-NA'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Chip
                            label={data.status}
                            size="small"
                            sx={{
                                borderRadius: 1.5,
                                height: 24,
                                fontSize: '0.65rem',
                                fontWeight: 'bold',
                                backgroundColor: data.status === 'Active' ? alpha('#22C55E', 0.1) : alpha('#EF4444', 0.1),
                                color: data.status === 'Active' ? '#16A34A' : '#DC2626',
                                border: '1px solid',
                                borderColor: data.status === 'Active' ? alpha('#22C55E', 0.2) : alpha('#EF4444', 0.2)
                            }}
                        />
                        <IconButton size="small" sx={{ ml: 1 }}><MoreVertIcon fontSize="small" /></IconButton>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <Avatar
                        src={data.logo || clgimg}
                        variant="rounded"
                        sx={{ width: 64, height: 64, borderRadius: 3, border: '1px solid #F1F5F9', bgcolor: '#F8FAFC' }}
                    >
                        <BusinessIcon />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight="1000" color="#1E293B" sx={{ lineHeight: 1.2, mb: 0.5 }}>{data.name}</Typography>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <LocationIcon sx={{ fontSize: 14, color: '#94A3B8' }} />
                            <Typography variant="caption" fontWeight="bold" color="#64748B">{data.city || 'Regional Center'}</Typography>
                        </Stack>
                    </Box>
                </Box>

                <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ mt: 'auto', pt: 3 }}>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={() => navigate('/college-details/detailed', { state: { college: data } })}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 'bold',
                            borderRadius: 3,
                            bgcolor: '#0F172A',
                            '&:hover': { bgcolor: '#1E293B' }
                        }}
                    >
                        Detailed View
                    </Button>
                </Stack>
            </Paper>
        </Grow>
    );
};

const ItiList = ({ data }) => {
    const theme = useTheme();
    const navigate = useNavigate();

    return (
        <Fade in={true}>
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 5, overflow: 'hidden' }}>
                <Table>
                    <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: '1000', color: '#64748B', py: 2.5 }}>ID/CODE</TableCell>
                            <TableCell sx={{ fontWeight: '1000', color: '#64748B' }}>INSTITUTE PROFILE</TableCell>
                            <TableCell sx={{ fontWeight: '1000', color: '#64748B' }}>LOCATION</TableCell>
                            <TableCell sx={{ fontWeight: '1000', color: '#64748B' }}>STATUS</TableCell>
                            <TableCell sx={{ fontWeight: '1000', color: '#64748B' }} align="right">INTERACTIONS</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map((row) => (
                            <TableRow key={row.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                                <TableCell sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>{row.code || row.id}</TableCell>
                                <TableCell>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Avatar src={row.logo} variant="rounded" sx={{ width: 40, height: 40, borderRadius: 1.5, border: '1px solid #F1F5F9' }} />
                                        <Typography variant="body2" fontWeight="1000" color="#1E293B">{row.name}</Typography>
                                    </Stack>
                                </TableCell>
                                <TableCell sx={{ color: '#475569', fontWeight: 'bold' }}>{row.city || 'N/A'}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={row.status}
                                        size="small"
                                        sx={{
                                            borderRadius: 1.5,
                                            fontWeight: 'bold',
                                            backgroundColor: row.status === 'Active' ? alpha('#22C55E', 0.1) : alpha('#EF4444', 0.1),
                                            color: row.status === 'Active' ? '#16A34A' : '#DC2626'
                                        }}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => navigate('/college-details/detailed', { state: { college: row } })}
                                        sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 'bold', px: 2 }}
                                    >
                                        Inspect
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Fade>
    );
};

const Collages = () => {
    const theme = useTheme();
    const [view, setView] = useState('grid');
    const [colleges, setColleges] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const fetchCollegesData = async () => {
            setLoading(true);
            try {
                const res = await getActiveColleges(searchTerm);
                if (res?.success) setColleges(res.data || []);
            } catch (error) {
                console.error("Failed to fetch colleges:", error);
            } finally {
                setLoading(false);
            }
        };

        const timer = setTimeout(() => {
            fetchCollegesData();
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    const filteredColleges = colleges; // Backend handles filtering

    return (
        <Box sx={{ p: { xs: 2, md: 5 }, minHeight: '100vh', bgcolor: '#F8FAFC' }}>
            {/* Header section */}
            <Fade in={true} timeout={600}>
                <Box sx={{ mb: 5, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { md: 'center' }, gap: 3 }}>
                    <Box>
                        <Typography variant="h4" fontWeight="1000" color="#0F172A" sx={{ letterSpacing: -1 }}>Campus Directory</Typography>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                            <BusinessIcon sx={{ fontSize: 16, color: '#64748B' }} />
                            <Typography variant="body2" color="#64748B" fontWeight="600">{filteredColleges.length} Active Partner ITIs</Typography>
                        </Stack>
                    </Box>
                    <Stack direction="row" spacing={2}>
                        <Button
                            variant="outlined"
                            startIcon={<FileDownloadIcon />}
                            sx={{ borderRadius: 3, textTransform: 'none', fontWeight: '1000', color: '#475569', borderColor: '#E2E8F0', bgcolor: 'white' }}
                        >
                            Export Roster
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            sx={{ borderRadius: 3, textTransform: 'none', fontWeight: '1000', bgcolor: '#0F172A', '&:hover': { bgcolor: '#1E293B' }, boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                        >
                            Onboard Facility
                        </Button>
                    </Stack>
                </Box>
            </Fade>

            {/* Filtering Controls */}
            <Grow in={true} timeout={800}>
                <Paper elevation={0} sx={{ p: 2, mb: 4, borderRadius: 5, border: '1px solid #E2E8F0', bgcolor: 'white', display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                    <TextField
                        placeholder="Search by name or code..."
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94A3B8' }} /></InputAdornment>,
                        }}
                        sx={{ minWidth: { xs: '100%', lg: 400 }, '& .MuiOutlinedInput-root': { borderRadius: 4, bgcolor: '#F8FAFC' } }}
                    />

                    <Stack direction="row" spacing={2} sx={{ width: { xs: '100%', lg: 'auto' }, justifyContent: 'flex-end' }}>
                        <Button
                            startIcon={<CalendarTodayIcon />}
                            variant="outlined"
                            size="small"
                            sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 'bold', color: '#1E293B', borderColor: '#E2E8F0', height: 40 }}
                        >
                            AY 2025-26
                        </Button>
                        <Box sx={{ display: 'flex', bgcolor: '#F1F5F9', p: 0.5, borderRadius: 3 }}>
                            <IconButton
                                size="small"
                                onClick={() => setView('grid')}
                                sx={{ borderRadius: 2, bgcolor: view === 'grid' ? 'white' : 'transparent', color: view === 'grid' ? theme.palette.primary.main : '#64748B', boxShadow: view === 'grid' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}
                            >
                                <GridViewIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={() => setView('list')}
                                sx={{ borderRadius: 2, bgcolor: view === 'list' ? 'white' : 'transparent', color: view === 'list' ? theme.palette.primary.main : '#64748B', boxShadow: view === 'list' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}
                            >
                                <FormatListBulletedIcon fontSize="small" />
                            </IconButton>
                        </Box>
                        <Button
                            startIcon={<FilterListIcon />}
                            variant="outlined"
                            size="small"
                            sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 'bold', color: '#1E293B', borderColor: '#E2E8F0', height: 40 }}
                        >
                            Advanced Filters
                        </Button>
                    </Stack>
                </Paper>
            </Grow>

            {/* Results section */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 15 }}>
                    <CircularProgress size={40} thickness={5} />
                </Box>
            ) : filteredColleges.length > 0 ? (
                view === 'grid' ? (
                    <Grid container spacing={3}>
                        {filteredColleges.map((item, index) => (
                            <Grid item size={{ xs: 12, sm: 6, lg: 4, xl: 3 }} key={item.id || index}>
                                <ItiCard data={{
                                    ...item,
                                    logo: item.logo || clgimg,
                                    status: item.isActive ? 'Active' : 'Inactive'
                                }} />
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <ItiList data={filteredColleges.map(c => ({
                        ...c,
                        logo: c.logo || clgimg,
                        status: c.isActive ? 'Active' : 'Inactive'
                    }))} />
                )
            ) : (
                <Stack spacing={2} alignItems="center" sx={{ py: 15 }}>
                    <Box sx={{ bgcolor: '#F1F5F9', p: 3, borderRadius: '50%' }}><BusinessIcon sx={{ fontSize: 64, color: '#94A3B8' }} /></Box>
                    <Typography variant="h6" fontWeight="1000" color="#475569">No colleges found</Typography>
                    <Typography variant="body2" color="#64748B">Try adjusting your search criteria or add a new college.</Typography>
                </Stack>
            )}
        </Box>
    );
};

export default Collages;
